import * as React from 'react';
import { useState, useEffect } from 'react';

import { SchoolInfoFormProps, SchoolOption } from './types';

import SchoolNameAutocomplete from './SchoolNameAutocomplete';

/**
 * 学校情報フォームコンポーネント (Shinsotsu Common用)
 * 新卒LP専用のため「現在のご状況」は常に在学中固定（バックエンドで設定）
 * 既存のフォームレイアウトに合わせたマークアップ
 */
const SchoolInfoForm: React.FC<SchoolInfoFormProps> = ({
  schoolType: initialSchoolType = '',
  schoolMasterId: initialSchoolMasterId,
  schoolName: initialSchoolName = '',
  schoolNameFree: initialSchoolNameFree = '',
  selectedSchool: initialSelectedSchool = null,
  majorSubjectId: initialMajorSubjectId,
  graduationYear: initialGraduationYear,
  majorSubjects = [],
  onSchoolTypeChange,
  onSchoolMasterChange,
  onSchoolNameFreeChange,
  onMajorSubjectChange,
  onGraduationYearChange,
  canEditSchoolInfo = true,
  errors = {}
}) => {
  const [schoolType, setSchoolType] = useState<string>(initialSchoolType);
  const [schoolMasterId, setSchoolMasterId] = useState<number | undefined>(initialSchoolMasterId);
  const [schoolName, setSchoolName] = useState<string>(initialSchoolName);
  const [schoolNameFree, setSchoolNameFree] = useState<string>(initialSchoolNameFree);
  const [graduationYear, setGraduationYear] = useState<number | undefined>(initialGraduationYear);
  const [selectedMajorSubject, setSelectedMajorSubject] = useState<number | undefined>(initialMajorSubjectId);
  const [selectedSchool, setSelectedSchool] = useState<SchoolOption | null>(initialSelectedSchool);

  const isPredictableSchoolType = ['1', '2', '3'].includes(schoolType);

  const selectedMajorSubjectObj = majorSubjects.find((ms) => ms.id === selectedMajorSubject);
  const topLevelMajorSubjects = majorSubjects.filter((ms) => !ms.parent_category);
  const otherMajorSubjects = majorSubjects.filter((ms) => ms.category === 'other' && ms.parent_category);
  const otherParentId = majorSubjects.find((ms) => ms.category === 'other' && !ms.parent_category)?.id;
  const isSubCategory = selectedMajorSubjectObj?.category === 'other' && selectedMajorSubjectObj?.parent_category;
  const isMajorSubjectOther =
    selectedMajorSubjectObj?.category === 'other' && !selectedMajorSubjectObj?.parent_category;
  const topLevelValue = isSubCategory ? otherParentId : selectedMajorSubject;

  useEffect(() => {
    setSchoolType(initialSchoolType);
    setSchoolMasterId(initialSchoolMasterId);
    setSchoolName(initialSchoolName);
    setSchoolNameFree(initialSchoolNameFree);
    setGraduationYear(initialGraduationYear);
    setSelectedMajorSubject(initialMajorSubjectId);
    setSelectedSchool(initialSelectedSchool);
  }, [
    initialSchoolType,
    initialSchoolMasterId,
    initialSchoolName,
    initialSchoolNameFree,
    initialGraduationYear,
    initialMajorSubjectId,
    initialSelectedSchool
  ]);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.updateHiddenFields) {
      // majorSubjectDetailId: set to selectedMajorSubject if detail is selected (isSubCategory),
      // set to empty string if "Other" is selected but detail is not (isMajorSubjectOther),
      // otherwise undefined (not visible)
      let majorSubjectDetailId: number | string | undefined = undefined;
      if (isSubCategory) {
        majorSubjectDetailId = selectedMajorSubject;
      } else if (isMajorSubjectOther) {
        majorSubjectDetailId = '';
      }

      // currentStatusは新卒LP固定のためReactからは送信しない（hidden fieldで固定値設定済み）
      window.updateHiddenFields({
        schoolType: schoolType,
        schoolMasterId: schoolMasterId,
        schoolName: schoolName,
        schoolNameFree: schoolNameFree,
        majorSubjectId: selectedMajorSubject,
        majorSubjectDetailId: majorSubjectDetailId,
        graduationYear: graduationYear
      });
    }
  }, [
    schoolType,
    schoolMasterId,
    schoolName,
    schoolNameFree,
    selectedMajorSubject,
    graduationYear,
    isSubCategory,
    isMajorSubjectOther
  ]);

  const handleSchoolTypeChange = (value: string) => {
    setSchoolType(value);
    setSchoolMasterId(undefined);
    setSchoolName('');
    setSchoolNameFree('');
    setSelectedSchool(null);
    onSchoolTypeChange?.(value);
  };

  const handleSchoolChange = (option: SchoolOption | null) => {
    setSelectedSchool(option);
    if (option) {
      setSchoolMasterId(option.value);
      setSchoolName(option.label);
      onSchoolMasterChange?.(option.value, option.label);
    } else {
      setSchoolMasterId(undefined);
      setSchoolName('');
      onSchoolMasterChange?.(0, '');
    }
  };

  const handleSchoolNameFreeChange = (value: string) => {
    setSchoolNameFree(value);
    onSchoolNameFreeChange?.(value);
  };

  const handleMajorSubjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    const subject = majorSubjects.find((ms) => ms.id === id);
    setSelectedMajorSubject(id);

    if (subject && subject.category === 'other' && !subject.parent_category) {
      onMajorSubjectChange?.(0);
    } else {
      onMajorSubjectChange?.(id);
    }
  };

  const handleMajorSubjectDetailChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    setSelectedMajorSubject(id);
    onMajorSubjectChange?.(id);
  };

  const handleGraduationYearChange = (year: number) => {
    setGraduationYear(year);
    onGraduationYearChange?.(year);
  };

  const graduationYearOptions = (() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // 0-indexed to 1-indexed

    // 9月以前なら当年から、10月以降なら翌年から
    const startOffset = currentMonth <= 9 ? 0 : 1;

    const years = [];
    for (let i = startOffset; i <= startOffset + 5; i++) {
      const year = currentYear + i;
      years.push({ value: year, label: `${year}年3月卒 (${String(year).slice(-2)}卒)` });
    }
    return years;
  })();

  return (
    <>
      <tr id="school-info-form">
        <th>
          学校情報
          <span
            className={`status_icon ${
              (errors.schoolType && errors.schoolType.length > 0) ||
              (errors.schoolName && errors.schoolName.length > 0) ||
              (errors.graduationYear && errors.graduationYear.length > 0)
                ? 'error_icon'
                : 'no_icon_yet'
            }`}
          ></span>
        </th>
        <td>
          <ul className="cf selectlist single">
            {/* 学校種別 */}
            <li className="success_check">
              <p className="school-info-label">学校種別</p>
              <select
                id="school_type"
                name="school_type"
                value={schoolType}
                onChange={(e) => handleSchoolTypeChange(e.target.value)}
                disabled={!canEditSchoolInfo}
                className={errors.schoolType && errors.schoolType.length > 0 ? 'must ipt-error' : 'must'}
              >
                <option value="">学校種別を選択</option>
                <option value="1">大学・大学院</option>
                <option value="2">短期大学</option>
                <option value="3">農業大学校</option>
                <option value="4">専門学校</option>
                <option value="5">高等学校・高専</option>
                <option value="6">その他</option>
              </select>
              <span className="baloon baloon_adress">【学校種別】を選択してください。</span>
              <label className="select_arrow"></label>
            </li>
            {errors.schoolType && errors.schoolType.length > 0 && <li className="error">{errors.schoolType[0]}</li>}

            {/* 学校名 */}
            {schoolType && (
              <>
                {!isPredictableSchoolType ? (
                  <li className="success_check">
                    <p className="school-info-label">学校名</p>
                    <input
                      type="text"
                      id="school_name_free"
                      name="school_name_free"
                      value={schoolNameFree || ''}
                      onChange={(e) => handleSchoolNameFreeChange(e.target.value)}
                      placeholder="例) 〇〇高等学校"
                      disabled={!canEditSchoolInfo}
                      className={errors.schoolName && errors.schoolName.length > 0 ? 'must ipt-error' : 'must'}
                      maxLength={255}
                    />
                    <span className="baloon baloon_tel">【学校名】を入力してください。</span>
                  </li>
                ) : (
                  <li className="success_check school-name-autocomplete-wrapper" style={{ position: 'relative' }}>
                    <p className="school-info-label">学校名 (※入力すると候補が表示されます)</p>
                    <SchoolNameAutocomplete
                      schoolType={schoolType}
                      value={selectedSchool}
                      onChange={handleSchoolChange}
                      isDisabled={!canEditSchoolInfo}
                      hasError={errors.schoolName && errors.schoolName.length > 0}
                    />
                    <span className="baloon baloon_tel">【学校名】を入力してください。</span>
                  </li>
                )}
                {errors.schoolName && errors.schoolName.length > 0 && <li className="error">{errors.schoolName[0]}</li>}
              </>
            )}

            {/* 専攻科目 */}
            {isPredictableSchoolType && (
              <>
                <li className="success_check">
                  <p className="school-info-label">専攻科目</p>
                  <select
                    id="major_subject_id"
                    name="major_subject_id"
                    value={topLevelValue || ''}
                    onChange={handleMajorSubjectChange}
                    disabled={!canEditSchoolInfo}
                    className={errors.majorSubjectId && errors.majorSubjectId.length > 0 ? 'must ipt-error' : 'must'}
                  >
                    <option value="">専攻科目を選択</option>
                    {topLevelMajorSubjects.map((ms) => (
                      <option key={ms.id} value={ms.id}>
                        {ms.displayName}
                      </option>
                    ))}
                  </select>
                  <span className="baloon baloon_adress">【専攻科目】を選択してください。</span>
                  <label className="select_arrow"></label>
                </li>
                {errors.majorSubjectId && errors.majorSubjectId.length > 0 && (
                  <li className="error">{errors.majorSubjectId[0]}</li>
                )}

                {/* 専攻科目詳細 */}
                {(isMajorSubjectOther || isSubCategory) && (
                  <li className="success_check">
                    <p className="school-info-label">専攻科目詳細</p>
                    <select
                      id="major_subject_detail_id"
                      name="major_subject_detail_id"
                      value={isSubCategory ? selectedMajorSubject || '' : ''}
                      onChange={handleMajorSubjectDetailChange}
                      disabled={!canEditSchoolInfo}
                      className="must"
                    >
                      <option value="">専攻科目詳細を選択</option>
                      {otherMajorSubjects.map((ms) => (
                        <option key={ms.id} value={ms.id}>
                          {ms.displayName}
                        </option>
                      ))}
                    </select>
                    <span className="baloon baloon_adress">【専攻科目詳細】を選択してください。</span>
                    <label className="select_arrow"></label>
                  </li>
                )}
              </>
            )}

            {/* 卒業見込み */}
            <li className="success_check">
              <p className="school-info-label">卒業見込み</p>
              <select
                id="graduation_year"
                name="graduation_year"
                value={graduationYear || ''}
                onChange={(e) => handleGraduationYearChange(Number(e.target.value))}
                disabled={!canEditSchoolInfo}
                className={errors.graduationYear && errors.graduationYear.length > 0 ? 'must ipt-error' : 'must'}
              >
                <option value="">選択してください</option>
                {graduationYearOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <span className="baloon baloon_adress">【卒業見込み】を選択してください。</span>
              <label className="select_arrow"></label>
              <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                ※卒業見込みが9月の場合は、同年度の「◯年3月卒」を選択してください。
              </small>
            </li>
            {errors.graduationYear && errors.graduationYear.length > 0 && (
              <li className="error">{errors.graduationYear[0]}</li>
            )}
          </ul>
        </td>
      </tr>
    </>
  );
};

export default SchoolInfoForm;
